import { useEffect, useState } from 'react';
import ReactMapGL, { Marker, NavigationControl, Popup } from 'react-map-gl';
import { Button, Col, Nav, NavItem, NavLink, Row, TabContent, TabPane } from 'reactstrap';
import Pagination from 'reactstrap/lib/Pagination';
import PaginationItem from 'reactstrap/lib/PaginationItem';
import PaginationLink from 'reactstrap/lib/PaginationLink';
import queryString from 'query-string';
import moment from 'moment';

import Address from 'Lib/components/common/Address';
import Currency from 'Lib/components/common/Currency';
import Date from 'Lib/components/common/Date';
import Image from 'Lib/components/common/Image';
import Spinner from 'Lib/components/common/Spinner';
import Text from 'Lib/components/common/Text';
import BathsField from 'Lib/components/form/BathsField';
import BedsField from 'Lib/components/form/BedsField';
import CurrencyField from 'Lib/components/form/CurrencyField';
import Form from 'Lib/components/form/Form';
import TextField from 'Lib/components/form/TextField';
import PropertyTypes from 'Lib/constants/property/propertyTypes';

const TABS = {
    List: 0,
    Map: 1
};

const List = ({ domElement }) => {
    const subdomain = domElement.getAttribute('data-subdomain');
    const defaultZoom = domElement.getAttribute('data-map-zoom');
    const pageSize = domElement.getAttribute('data-page-size') ?? 21;
    const defaultFilters = queryString.parse(domElement.getAttribute('data-filter'));

    const [loading, setLoading] = useState(true);
    const [popupInfo, setPopupInfo] = useState(null);
    const [tab, setTab] = useState(TABS.List);
    const [pagination, setPagination] = useState({});
    const [viewport, setViewport] = useState({
        zoom: defaultZoom ?? 8
    });
    const [searchFilters, setSearchFilters] = useState({ page: 1, pageSize: 15 });
    const [data, setData] = useState([]);

    useEffect(() => {
        fetchListings();
    }, []);

    const fetchListings = filters => {
        const qs = queryString.stringify({ ...defaultFilters, ...filters, pageSize });

        setSearchFilters({ ...filters, pageSize });
        setLoading(true);

        fetch(`https://${subdomain}${process.env.DOMAIN}/api/public/listings?${qs}`)
            .then(response => {
                const page = parseInt(response.headers.get('pagination-current-page'));
                const pageSize = parseInt(response.headers.get('pagination-page-size'));
                const totalItems = parseInt(response.headers.get('pagination-total-items'));
                const totalPages = parseInt(response.headers.get('pagination-total-pages'));
                const pages = [];

                let lastVisiblePage = page + 10;
                if (totalPages > lastVisiblePage) {
                    lastVisiblePage = totalPages;
                }

                let firstPage = page - 5;
                let pagesAfter = 5;
                if (firstPage <= 0) {
                    pagesAfter += Math.abs(firstPage);
                    firstPage = 1;
                }

                let lastPage = page + pagesAfter;
                if (lastPage > totalPages) {
                    lastPage = totalPages;
                }

                for (var i = firstPage; i <= lastPage; i++) {
                    pages.push(i);
                }

                setPagination({
                    page,
                    pageSize,
                    totalItems,
                    totalPages,
                    pages
                });

                return response.json();
            })
            .then(data => {
                setData(data);
                if (data.length > 0) {
                    setViewport({
                        latitude: parseFloat(data[0].unit.latitude),
                        longitude: parseFloat(data[0].unit.longitude),
                        zoom: viewport?.zoom ?? defaultZoom ?? 8
                    });
                }
                setLoading(false);
            });
    };

    const handleSearch = values => {
        fetchListings({ ...values, page: 1, pageSize });
    };

    const goToListing = listing => {
        const l = document.createElement('a');
        l.href = `?propertyListingID=${listing.propertyListingID}`;
        document.body.appendChild(l);
        l.click();
        document.body.removeChild(l);
    };

    const renderList = () => (
        <div style={{ paddingTop: '3vh' }}>
            <Row>
                {data.map(({ image, listing, property, unit }, index) => (
                    <Col key={index} md="4">
                        <div className="listings-container">
                            <div className="listing-card --hover" onClick={() => goToListing(listing)}>
                                <Row>
                                    <div className="listing-image-box">
                                        {image.large ? <Image className="listing-image" src={image.large} /> : <div className="listing-no-image">No Image</div>}
                                    </div>
                                </Row>
                                <Row>
                                    <div className="listings-card-body">
                                        <div className="listings-amount">
                                            <Currency className="listing-rent-amount" value={unit.rent} /> <span className="listing-rent-frequency">USD / Month</span>
                                        </div>

                                        <div className="listings-information">
                                            <div>
                                                <i className="fas fa-bed" /> {unit.beds} bd
                                            </div>
                                            <div>
                                                <i className="fas fa-bath" /> {unit.baths} ba
                                            </div>
                                            {listing.size && listing.size > 0 && (
                                                <div>
                                                    <i className="fas fa-ruler" /> {listing.size || 0} sqft
                                                </div>
                                            )}
                                        </div>

                                        <div className="listings-details">
                                            <div>
                                                <Address displayInline value={unit} />
                                            </div>

                                            <div className="listings-property-type">
                                                <Text value={PropertyTypes.getName(property.propertyTypeID)} />
                                            </div>
                                            {listing.availabilityDate && (
                                                <div className="listings-availability">
                                                    Available: {moment(listing.availabilityDate).isAfter(moment()) ? <Date value={listing.availabilityDate} /> : 'Immediately'}
                                                </div>
                                            )}
                                            <div>
                                                <Button className="listings-view-details">View Details</Button>
                                            </div>
                                        </div>
                                    </div>
                                </Row>
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>
            {pagination.totalPages > 1 && (
                <div className="listing-pagination">
                    <Pagination>
                        {pagination.page != 1 && (
                            <PaginationItem>
                                <PaginationLink
                                    previous
                                    href="#"
                                    onClick={e => {
                                        e.preventDefault();
                                        fetchListings({ ...searchFilters, page: pagination.page - 1 });
                                    }}
                                />
                            </PaginationItem>
                        )}
                        {pagination.pages.map(i => {
                            return (
                                <PaginationItem key={i}>
                                    <PaginationLink
                                        href="#"
                                        onClick={e => {
                                            e.preventDefault();
                                            fetchListings({ ...searchFilters, page: i });
                                        }}
                                    >
                                        {i}
                                    </PaginationLink>
                                </PaginationItem>
                            );
                        })}
                        {pagination.page != pagination.totalPages && (
                            <PaginationItem>
                                <PaginationLink
                                    next
                                    href="#"
                                    onClick={e => {
                                        e.preventDefault();
                                        fetchListings({ ...searchFilters, page: pagination.page + 1 });
                                    }}
                                />
                            </PaginationItem>
                        )}
                    </Pagination>
                </div>
            )}
        </div>
    );

    const renderMap = () => {
        return (
            <div style={{ paddingTop: '3vh' }}>
                <ReactMapGL
                    {...viewport}
                    width="100%"
                    height="50vh"
                    mapStyle="mapbox://styles/mapbox/streets-v11"
                    mapboxAccessToken={process.env.REACT_MAP_GL_KEY}
                    onViewportChange={setViewport}
                >
                    <div style={{ position: 'absolute', left: 10, top: 10 }}>
                        <NavigationControl showCompass={false} />
                    </div>

                    {data.map(({ image, listing, unit }, index) => {
                        const unitLatitude = parseFloat(unit.latitude);
                        const unitLongitude = parseFloat(unit.longitude);

                        return (
                            <Marker
                                key={`${unit.unitID}_${index}`}
                                latitude={unitLatitude}
                                longitude={unitLongitude}
                                onClick={() => setPopupInfo({ ...image, ...listing, ...unit })}
                            >
                                <i className="fas fa-map-marker listing-mapbox-marker" />
                            </Marker>
                        );
                    })}

                    {popupInfo && (
                        <Popup
                            className="listing-mapbox-popup"
                            anchor="top"
                            tipSize={5}
                            closeOnClick={false}
                            onClose={() => setPopupInfo(null)}
                            latitude={parseFloat(popupInfo.latitude)}
                            longitude={parseFloat(popupInfo.longitude)}
                        >
                            <div className="listing-popup-container" onClick={() => goToListing(popupInfo)}>
                                <div className="listing-popup-image">
                                    {popupInfo.large ? <Image className="listing-image" src={popupInfo.large} /> : <div className="listing-no-image">No Image</div>}
                                </div>
                                <div className="listing-popup-details" style={{ display: 'flex', justifyContent: 'space-around' }}>
                                    <div>
                                        <Currency value={popupInfo.rent} />
                                    </div>
                                    |
                                    <div>
                                        <Text value={popupInfo.beds} />
                                        {'  '} Beds
                                    </div>
                                    |
                                    <div>
                                        <Text value={popupInfo.baths} />
                                        {'  '} Baths
                                    </div>
                                </div>
                            </div>
                        </Popup>
                    )}
                </ReactMapGL>
            </div>
        );
    };

    return (
        <div className="rv-widget">
            <Form
                onSubmit={handleSearch}
                render={form => (
                    <form onSubmit={form.handleSubmit}>
                        <div className="listings-search-form">
                            <Row>
                                <Col md="4">
                                    <TextField id="search" name="search" label="Search" helperText="Search By: address, city, postal code, description" />
                                </Col>
                                <Col md="4">
                                    <div className="split-column-fields">
                                        <CurrencyField style={{ width: '45%' }} id="rentMin" name="rentMin" label="Minimum Rent" />
                                        <CurrencyField style={{ width: '45%' }} id="rentMax" name="rentMax" label="Maximum Rent" />
                                    </div>
                                </Col>
                                <Col md="4">
                                    <div className="split-column-fields">
                                        <BedsField style={{ width: '45%' }} id="bedsMin" name="bedsMin" label="Beds" />
                                        <BathsField style={{ width: '45%' }} id="bathsMin" name="bathsMin" label="Baths" />
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="listings-search-form-submit" md="12">
                                    <Button type="submit">Submit</Button>
                                </Col>
                            </Row>
                        </div>
                    </form>
                )}
            />
            {loading ? (
                <Spinner />
            ) : data.length == 0 ? (
                <div className="empty-listings">No listings found</div>
            ) : (
                <>
                    <Nav tabs>
                        {Object.entries(TABS).map(([key, value], index) => (
                            <NavItem key={index}>
                                <NavLink
                                    className={tab == value ? 'active' : ''}
                                    onClick={() => {
                                        setTab(value);
                                        setTimeout(() => {
                                            setViewport({ ...viewport, latitude: viewport.latitude + 0.0001 });
                                        });
                                    }}
                                >
                                    {key}
                                </NavLink>
                            </NavItem>
                        ))}
                    </Nav>
                    <TabContent activeTab={tab}>
                        <TabPane tabId={TABS.List}>{renderList()}</TabPane>
                        <TabPane tabId={TABS.Map}>{renderMap()}</TabPane>
                    </TabContent>
                </>
            )}
        </div>
    );
};

export default List;
